import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import queryString from 'query-string';
import { getOrderList } from '../../actions/adminOrdersDash';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';

// --- Sub-components ---

const SearchBar = ({
  queryInput,
  parsedQuery,
  onInputChange,
  onSubmit,
  onReset,
}: {
  queryInput: string;
  parsedQuery: string;
  onInputChange: (e: any) => void;
  onSubmit: (e: any) => void;
  onReset: () => void;
}) => (
  <div className="admin-card-body" style={{ borderBottom: '1px solid #e1e3e5' }}>
    <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8 }}>
      <input
        className="form-control form-control-sm"
        placeholder="Search orders…"
        value={queryInput}
        onChange={onInputChange}
        style={{ maxWidth: 300, fontSize: '0.82rem' }}
      />
    </form>
    {parsedQuery && (
      <div style={{ marginTop: 8 }}>
        <span
          className="status-badge cancelled"
          style={{ cursor: 'pointer', fontSize: '0.75rem' }}
          onClick={onReset}
        >
          {parsedQuery} &times;
        </span>
      </div>
    )}
  </div>
);

const FilterDropdown = ({
  label,
  isOpen,
  onToggle,
  children,
}: {
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => (
  <div className="admin-dropdown-wrap">
    <button className="admin-dropdown-trigger" onClick={onToggle} type="button">
      {label} <i className="fas fa-chevron-down" style={{ fontSize: '0.6rem', marginLeft: 4 }} />
    </button>
    {isOpen && <div className="admin-dropdown-menu">{children}</div>}
  </div>
);

const FilterBar = ({
  filter,
  orderStatus,
  shippingType,
  dateOpen,
  orderStatusOpen,
  shippingTypeOpen,
  setDateOpen,
  setOrderStatusOpen,
  setShippingTypeOpen,
  onSetFilter,
  onOrderStatus,
  onShippingType,
  onReset,
}: any) => (
  <div
    className="admin-card-body"
    style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, borderBottom: '1px solid #e1e3e5' }}
  >
    <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6d7175', textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 4 }}>
      Filter
    </span>

    <FilterDropdown
      label={(filter === 'newest' && 'Newest') || (filter === 'oldest' && 'Oldest') || 'Date'}
      isOpen={dateOpen}
      onToggle={() => setDateOpen(!dateOpen)}
    >
      <button className={`admin-dropdown-item${filter === 'newest' ? ' active' : ''}`} onClick={() => { onSetFilter('newest'); setDateOpen(false); }}>Newest</button>
      <button className={`admin-dropdown-item${filter === 'oldest' ? ' active' : ''}`} onClick={() => { onSetFilter('oldest'); setDateOpen(false); }}>Oldest</button>
    </FilterDropdown>

    <FilterDropdown
      label={(orderStatus === 'Confirmed' && 'Confirmed') || (orderStatus === 'Processing' && 'Processing') || (orderStatus === 'Cancelled' && 'Cancelled') || 'Order Status'}
      isOpen={orderStatusOpen}
      onToggle={() => setOrderStatusOpen(!orderStatusOpen)}
    >
      {orderStatus !== '' && <button className="admin-dropdown-item" onClick={() => { onOrderStatus(''); setOrderStatusOpen(false); }}>All</button>}
      <button className={`admin-dropdown-item${orderStatus === 'Confirmed' ? ' active' : ''}`} onClick={() => { onOrderStatus('Confirmed'); setOrderStatusOpen(false); }}>Confirmed</button>
      <button className={`admin-dropdown-item${orderStatus === 'Processing' ? ' active' : ''}`} onClick={() => { onOrderStatus('Processing'); setOrderStatusOpen(false); }}>Processing</button>
      <button className={`admin-dropdown-item${orderStatus === 'Cancelled' ? ' active' : ''}`} onClick={() => { onOrderStatus('Cancelled'); setOrderStatusOpen(false); }}>Cancelled</button>
    </FilterDropdown>

    <FilterDropdown
      label={(shippingType === 'pickup' && 'Pick up') || (shippingType === 'delivery' && 'Delivery') || 'Shipping Type'}
      isOpen={shippingTypeOpen}
      onToggle={() => setShippingTypeOpen(!shippingTypeOpen)}
    >
      {shippingType !== '' && <button className="admin-dropdown-item" onClick={() => { onShippingType(''); setShippingTypeOpen(false); }}>All</button>}
      <button className={`admin-dropdown-item${shippingType === 'pickup' ? ' active' : ''}`} onClick={() => { onShippingType('pickup'); setShippingTypeOpen(false); }}>Pick up</button>
      <button className={`admin-dropdown-item${shippingType === 'delivery' ? ' active' : ''}`} onClick={() => { onShippingType('delivery'); setShippingTypeOpen(false); }}>Delivery</button>
    </FilterDropdown>

    <button className="admin-dropdown-trigger" onClick={onReset} type="button">
      Reset
    </button>
  </div>
);

const ucFirst = (str: string) =>
  str.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const customerType = (orderCount: number) => {
  if (orderCount <= 1) return { label: 'New', color: '#6d7175', bg: '#f1f1f1' };
  if (orderCount > 5) return { label: 'Loyal', color: '#065f46', bg: '#d1fae5' };
  return { label: 'Returning', color: '#004085', bg: '#cce5ff' };
};

const OrdersTable = ({ orders, onRowClick }: { orders: any[]; onRowClick: (o: any) => void }) => (
  <div className="table-responsive">
    <table className="admin-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Time</th>
          <th>Total</th>
          <th>Shipping</th>
          <th>Customer</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((order: any) => {
          const ct = customerType(order.user.orders.length);
          return (
            <tr key={order._id} onClick={() => onRowClick(order)}>
              <td style={{ fontWeight: 600 }}>{ucFirst(order.firstName)} {ucFirst(order.lastName)}</td>
              <td className="order-email">{order.email}</td>
              <td>{order.phoneNumber}</td>
              <td className="order-time">{moment(order.created_at).fromNow()}</td>
              <td>AUD {(order.total / 100).toFixed(2)}</td>
              <td>{ucFirst(order.shippingType)}</td>
              <td>
                <span className="status-badge" style={{ background: ct.bg, color: ct.color }}>
                  {ct.label}
                </span>
              </td>
              <td>
                <span className={`status-badge ${order.orderStatus.toLowerCase()}`}>
                  {order.orderStatus}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const Pagination = ({
  currentPage,
  totalPages,
  onPrevious,
  onNext,
}: {
  currentPage: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
}) => (
  <div className="admin-card-body" style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
    <button
      className="admin-dropdown-trigger"
      onClick={onPrevious}
      disabled={currentPage <= 1}
      style={{ opacity: currentPage <= 1 ? 0.4 : 1 }}
    >
      Previous
    </button>
    <button
      className="admin-dropdown-trigger"
      onClick={onNext}
      disabled={currentPage >= totalPages}
      style={{ opacity: currentPage >= totalPages ? 0.4 : 1 }}
    >
      Next
    </button>
  </div>
);

// --- Main component ---

const OrdersDashboard = (props: any) => {
  const history = useHistory();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState(location.search);
  const [filter, setFilterState] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const [orderStatusOpen, setOrderStatusOpen] = useState(false);
  const [shippingTypeOpen, setShippingTypeOpen] = useState(false);
  const [orderStatus, setOrderStatus] = useState('');
  const [shippingType, setShippingType] = useState('');

  const searchQueryInitialized = useRef(false);
  const locationSearchInitialized = useRef(false);

  const updateSearchQuery = (parsed: any) => setSearchQuery('?' + queryString.stringify(parsed));

  useEffect(() => {
    const parsed = queryString.parse(location.search) as any;
    let page: any = 1, filterVal: any, query: any, orderStatusVal: any, shippingTypeVal: any;

    if (parsed.orderStatus) { orderStatusVal = parsed.orderStatus; setOrderStatus(orderStatusVal); }
    if (parsed.shippingType) { shippingTypeVal = parsed.shippingType; setShippingType(shippingTypeVal); }
    if (parsed.page) { page = parsed.page; }
    if (parsed.filter) { filterVal = parsed.filter; setFilterState(filterVal); }
    if (parsed.query) { query = parsed.query; setQueryInput(''); }

    props.getOrderList(page, filterVal, query, orderStatusVal, undefined, shippingTypeVal);
  }, []);

  useEffect(() => {
    if (!searchQueryInitialized.current) { searchQueryInitialized.current = true; return; }
    history.push(`/adminpanel/orders${searchQuery}`);
  }, [searchQuery]);

  useEffect(() => {
    if (!locationSearchInitialized.current) { locationSearchInitialized.current = true; return; }
    const parsed = queryString.parse(location.search) as any;
    let page: any, filterVal: any, query: any, orderStatusVal: any, shippingTypeVal: any;

    setShippingType(parsed.shippingType ? (shippingTypeVal = parsed.shippingType) : '');
    setOrderStatus(parsed.orderStatus ? (orderStatusVal = parsed.orderStatus) : '');
    setFilterState(parsed.filter ? (filterVal = parsed.filter) : '');
    if (parsed.page) page = parsed.page;
    if (parsed.query) query = parsed.query;

    updateSearchQuery(parsed);
    window.scrollTo(0, 0);
    props.getOrderList(page, filterVal, query, orderStatusVal, undefined, shippingTypeVal);
  }, [location.search]);

  const modifyQuery = (changes: Record<string, any>) => {
    const parsed = queryString.parse(location.search) as any;
    if (parsed.page) parsed.page = 1;
    Object.assign(parsed, changes);
    updateSearchQuery(parsed);
  };

  const setFilter = (value: string) => { setFilterState(value); modifyQuery({ filter: value }); };
  const handleShippingType = (value: string) => { setShippingType(value); modifyQuery({ shippingType: value || undefined }); };
  const handleOrderStatus = (value: string) => { setOrderStatus(value); modifyQuery({ orderStatus: value || undefined }); };

  const handleReset = () => {
    const parsed = queryString.parse(location.search) as any;
    if (parsed.page) parsed.page = 1;
    Object.assign(parsed, { query: undefined, orderStatus: undefined, filter: undefined, shippingType: undefined });
    setQueryInput('');
    updateSearchQuery(parsed);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const parsed = queryString.parse(location.search) as any;
    parsed.query = queryInput;
    parsed.page = undefined;
    setQueryInput('');
    updateSearchQuery(parsed);
  };

  const previousPage = () => {
    const parsed = queryString.parse(location.search) as any;
    parsed.page = parseInt(parsed.page as string) - 1;
    updateSearchQuery(parsed);
  };

  const nextPage = () => {
    const parsed = queryString.parse(location.search) as any;
    parsed.page = parsed.page ? parseInt(parsed.page as string) + 1 : 2;
    updateSearchQuery(parsed);
  };

  const routeToOrder = (order: any) =>
    history.push({ pathname: `/adminpanel/orders/${order._id}`, state: { order } });

  const parsed = queryString.parse(location.search) as any;
  const PAGE_SIZE = 24;
  const totalPages = Math.ceil(props.adminOrders.orderCount / PAGE_SIZE);
  const currentPage = parsed.page
    ? parsed.page > totalPages ? totalPages : parsed.page <= 1 ? 1 : parseInt(parsed.page)
    : 1;
  const end = Math.min(PAGE_SIZE * currentPage, props.adminOrders.orderCount);
  const startItem = currentPage <= 1 ? 1 : (currentPage - 1) * PAGE_SIZE + 1;

  return (
    <div>
      <Helmet>
        <title>Orders - Frozberries Victoria</title>
        <meta name="description" content="Orders - Frozberries Victoria" />
      </Helmet>

      {props.adminOrders.loading ? (
        <div className="admin-empty-state">
          <i className="fas fa-spinner fa-spin" />
          <p>Loading orders…</p>
        </div>
      ) : (
        <div className="admin-card">
          <div className="admin-card-header">
            <p className="admin-card-title">Orders</p>
            {props.adminOrders.orderCount > 0 && (
              <span style={{ fontSize: '0.78rem', color: '#6d7175' }}>
                {startItem}–{end} of {props.adminOrders.orderCount}
              </span>
            )}
          </div>

          <SearchBar
            queryInput={queryInput}
            parsedQuery={parsed.query}
            onInputChange={(e) => setQueryInput(e.target.value)}
            onSubmit={handleSubmit}
            onReset={handleReset}
          />

          <FilterBar
            filter={filter}
            orderStatus={orderStatus}
            shippingType={shippingType}
            dateOpen={dateOpen}
            orderStatusOpen={orderStatusOpen}
            shippingTypeOpen={shippingTypeOpen}
            setDateOpen={setDateOpen}
            setOrderStatusOpen={setOrderStatusOpen}
            setShippingTypeOpen={setShippingTypeOpen}
            onSetFilter={setFilter}
            onOrderStatus={handleOrderStatus}
            onShippingType={handleShippingType}
            onReset={handleReset}
          />

          {props.adminOrders.orderCount === 0 ? (
            <div className="admin-empty-state">
              <i className="fas fa-shopping-bag" />
              <p>No orders found.</p>
            </div>
          ) : (
            <>
              <div className="admin-card-body-flush">
                <OrdersTable orders={props.adminOrders.orders} onRowClick={routeToOrder} />
              </div>

              {totalPages > 1 && props.adminOrders.orders.length !== 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPrevious={previousPage}
                  onNext={nextPage}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  adminOrders: state.adminOrders,
});

export default connect(mapStateToProps, { getOrderList })(OrdersDashboard);
